import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { TagColorPalette } from '@blaze-runtime/engine-domain';
import { TagItemInfo } from '@blaze-shared/api-contracts-domain';
import { urlUtils } from '@blaze-shared/platform-util';

import {
  CellValueUpdateFromTag,
  CellValueUpdateModelForTag,
  type InlineEditorConfig,
  type InlineEditorLayoutMode,
} from '../base-editor/inline-editor.types';

@UntilDestroy()
@Component({
  selector: 'be-tag-menu',
  templateUrl: './tag-menu.component.html',
  styleUrls: ['./tag-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TableTagMenuComponent implements AfterViewInit, OnChanges {
  valueChange = output<CellValueUpdateFromTag | string | number | string[] | null | undefined>();

  @Input() config?: InlineEditorConfig;
  @Input() layoutMode?: InlineEditorLayoutMode;
  @Input() value: string | number | string[] | null | undefined;

  @ViewChild('filterInput') filterInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger | undefined;

  private defaultThemePalette: TagColorPalette | undefined;

  searchFormCtrl = new UntypedFormControl();
  possibleNewValues: TagItemInfo[] | undefined = undefined;

  readonly getFileUrlHref = urlUtils.getFileUrlHref;

  get isInputLike(): boolean {
    return this.layoutMode === 'input-like';
  }

  get isMultiple(): boolean {
    return !!this.config?.columnSchema?.multipleValues;
  }

  get palette(): TagColorPalette | undefined {
    return (
      (this.config?.detailData as CellValueUpdateModelForTag)?.palette || this.defaultThemePalette
    );
  }

  get isAllowedToCreateNewValues(): boolean {
    return this.config?.columnSchema?.tagDefinition?.allowDynamicValues ?? false;
  }

  constructor() {
    this.defaultThemePalette = this.createValuesColorsFromConfig();

    this.searchFormCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.recalculatePossibleNewValues();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.config) {
      this.defaultThemePalette = this.createValuesColorsFromConfig();
    }
  }

  ngAfterViewInit(): void {
    this.recalculatePossibleNewValues();
    if (this.layoutMode !== 'input-like') {
      this.openPickerMenu();
    }
  }

  recalculatePossibleNewValues(): void {
    this.possibleNewValues = this.config?.columnSchema?.tagDefinition?.items
      .filter((val) =>
        Array.isArray(this.value) ? !this.value.includes(val.value) : val.value !== this.value,
      )
      .filter((val) =>
        this.searchFormCtrl.value
          ? val.value.toLowerCase().includes(this.searchFormCtrl.value.toLowerCase())
          : true,
      );

    this.focusFilter();
  }

  addTag(event: Event, tag: TagItemInfo): void {
    if (this.layoutMode === 'input-like') {
      if (this.isMultiple) {
        if (Array.isArray(this.value)) {
          this.value = [...this.value, tag.value];
        } else {
          this.value = [tag.value];
        }
      } else {
        this.value = tag.value;
      }
      this.valueChange.emit(this.value);
    } else {
      this.valueChange.emit({ actionType: 'add', tag: tag.value });
    }
    event.preventDefault();
    this.recalculatePossibleNewValues();
  }

  preventEventAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  removeTag(tag: string): void {
    if (this.isMultiple) {
      if (Array.isArray(this.value)) {
        this.value = this.value.filter((v) => v !== tag);
      } else {
        this.value = [];
      }
    } else {
      this.value = undefined;
    }
    this.valueChange.emit(this.value);
    this.recalculatePossibleNewValues();
  }

  createTag(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      if (this.searchFormCtrl.value.length) {
        this.valueChange.emit({ actionType: 'create', tag: this.searchFormCtrl.value });
      }
      event.preventDefault();
    }
    this.recalculatePossibleNewValues();
  }

  externalClickAction(event: CellValueUpdateFromTag): void {
    if (event.actionType === 'remove') {
      this.removeTag(event.tag);
    } else {
      this.openPickerMenu();
    }
  }

  focusFilter(): void {
    setTimeout(() => {
      if (this.filterInput) {
        this.menuTrigger?.menu?.resetActiveItem();
        const input = this.filterInput.nativeElement;
        const inputLength = input.value.length;
        input.focus();
        input.select();
        input.setSelectionRange(inputLength, inputLength);
      }
    });
  }

  private openPickerMenu(): void {
    this.menuTrigger?.openMenu();
  }

  private createValuesColorsFromConfig(): TagColorPalette {
    const valuesMap = this.config?.columnSchema?.tagDefinition?.items
      .map((tagDefItem) => {
        return {
          [tagDefItem.value]: {
            foreground: tagDefItem.foregroundColor,
            background: tagDefItem.backgroundColor,
          },
        };
      })
      .reduce((prev, curr) => ({ ...prev, ...curr }), {});

    return {
      type: 'schema',
      valuesMap,
    };
  }
}
